<template>
  <modal
    :name="modalName"
    width="94%"
    height="auto"
    :max-width="1280"
    :adaptive="true"
    :scrollable="true"
    @opened="focusClose"
    @before-close="beforeClose"
  >
    <div
      class="automation-guide-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="automation-guide-title"
      @keyup.esc.stop.prevent="$emit('close')"
    >
      <AppModalHeader
        ref="header"
        design="new"
        title="How Follow-up Automation works"
        eyebrow="Follow-up Automation"
        title-id="automation-guide-title"
        @close="$emit('close')"
      />

      <AppTabs
        class="automation-guide-tabs"
        design="new"
        :tabs="tabs"
        :value="activeTab"
        @input="activeTab = $event"
      />

      <main class="automation-guide-body">
        <section v-if="activeTab === 'overview'" class="automation-guide-overview">
          <div class="automation-guide-intro">
            <strong>One run is a historical plan, not a live copy of the lead.</strong>
            <span>
              Follow-up Automation creates a schedule from a specific outbound communication and saves the
              business context that existed at that moment.
            </span>
          </div>

          <div class="automation-guide-grid">
            <article class="automation-guide-card">
              <span class="automation-guide-step">1</span>
              <div>
                <h3>Anchor and start</h3>
                <p>
                  The anchor is the latest regular outbound communication recorded for the lead. It must be
                  recorded, sent, delivered or read. Messages created by an earlier follow-up run are not eligible
                  as anchors.
                </p>
                <ul>
                  <li>
                    The lead must use Lead Flow v2 and be in an open First Contact, Engaged, Consult, Offer
                    Negotiation or Invoice Negotiation stage.
                  </li>
                  <li>Automatic start also requires an assigned manager and enabled automation.</li>
                  <li>Another active follow-up run must not already exist for the lead.</li>
                  <li>No customer inbound may exist at or after the selected anchor.</li>
                  <li>The same outbound communication cannot automatically start another run.</li>
                </ul>
              </div>
            </article>

            <article class="automation-guide-card">
              <span class="automation-guide-step">2</span>
              <div>
                <h3>Schedule and snapshot</h3>
                <p>
                  The lead stage selects a versioned policy. Its offsets are calculated from the anchor, then the
                  delivery window and timezone rules are applied. At creation, the run saves the stage, policy
                  version, manager and anchor; each delivery saves its provider, channel, template, variables and
                  due time.
                </p>
                <p>
                  These values form a snapshot. Later changes to the lead, manager or template do not rewrite that
                  run. A rebuild cancels the active run and creates a new one; the previous run remains in history.
                </p>
              </div>
            </article>

            <article class="automation-guide-card">
              <span class="automation-guide-step">3</span>
              <div>
                <h3>What may change</h3>
                <p>
                  Delivery statuses change as the run executes. A customer inbound completes the active run and
                  cancels its remaining planned deliveries. A stage restart or rebuild also closes the old run
                  without rewriting its snapshot.
                </p>
                <p>
                  <strong>Delay is the only in-place schedule edit.</strong> It moves every remaining planned item by
                  the same duration. Sent, sending and historical items are not changed. A reason is required, the
                  new time must be in the future and the maximum delay is 14 days.
                </p>
              </div>
            </article>

            <article class="automation-guide-card">
              <span class="automation-guide-step">4</span>
              <div>
                <h3>Permissions</h3>
                <dl class="automation-guide-roles">
                  <div>
                    <dt>General Manager, Sales Supervisor, Marketing Supervisor</dt>
                    <dd>
                      Can manage automation: start, pause or resume, enable or disable, send the next item now and
                      delay the remaining schedule. They may delay a run more than once.
                    </dd>
                  </div>
                  <div>
                    <dt>Assigned Sales Manager</dt>
                    <dd>
                      Can delay an enabled active run for their own Lead Flow v2 lead once per run. They cannot
                      start, disable or send the next item immediately.
                    </dd>
                  </div>
                  <div>
                    <dt>System</dt>
                    <dd>
                      Starts eligible runs automatically, dispatches due items and stops an active run after a
                      customer inbound.
                    </dd>
                  </div>
                </dl>
              </div>
            </article>
          </div>
        </section>

        <section v-else class="automation-guide-errors">
          <p class="automation-guide-note">
            This catalog is provided by the backend and shows every Needs Attention reason currently recognized by
            Follow-up Automation.
          </p>
          <div class="automation-legend-levels">
            <span><AppStatusBadge design="new" compact tone="amber" label="Not sent" /> Provider was not contacted.</span>
            <span><AppStatusBadge design="new" compact tone="red" label="Send failed" /> A provider request or delivery event failed.</span>
          </div>
          <details
            v-for="group in groups"
            :key="group.code"
            class="automation-legend-group"
            :open="group.code === 'contact_data'"
          >
            <summary>
              <strong>{{ group.name }}</strong>
              <span>{{ group.reasons.length }} {{ group.reasons.length === 1 ? 'error' : 'errors' }}</span>
            </summary>
            <div class="automation-table-scroll">
              <table class="automation-legend-table">
                <thead>
                  <tr>
                    <th>Error</th>
                    <th>Detected at</th>
                    <th>Provider request</th>
                    <th>Automatic retry</th>
                    <th>Owner</th>
                    <th>Recommended action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="reason in group.reasons" :key="reason.code">
                    <td>
                      <strong>{{ reason.title }}</strong>
                      <small>{{ reason.code }}</small>
                      <span>{{ reason.explanation }}</span>
                    </td>
                    <td>{{ reason.phase.name }}</td>
                    <td>
                      <AppStatusBadge
                        design="new"
                        compact
                        :tone="reason.provider_attempted ? 'red' : 'amber'"
                        :label="reason.provider_attempted ? 'Yes' : 'No'"
                      />
                    </td>
                    <td>{{ reason.automatic_retry_scheduled ? 'Scheduled' : 'Not scheduled' }}</td>
                    <td>{{ reason.owner }}</td>
                    <td>{{ reason.recommended_action }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </details>
        </section>
      </main>
    </div>
  </modal>
</template>

<script>
import AppTabs from '@/components/AppTabs'

export default {
  name: 'FollowUpAutomationAttentionLegend',
  components: { AppTabs },
  props: {
    categories: { type: Array, required: true },
    reasons: { type: Array, required: true }
  },
  data: () => ({
    activeTab: 'overview',
    tabs: [
      { value: 'overview', label: 'How it works' },
      { value: 'errors', label: 'Errors' }
    ]
  }),
  computed: {
    modalName() {
      return 'follow-up-automation-guide'
    },
    groups() {
      return this.categories.map(category => ({
        ...category,
        reasons: this.reasons.filter(reason => reason.category.code === category.code)
      }))
    }
  },
  mounted() {
    this.$modal.show(this.modalName)
  },
  methods: {
    focusClose() {
      this.$refs.header.focusClose()
    },
    beforeClose(event) {
      event.cancel()
      this.$emit('close')
    }
  }
}
</script>

<style scoped lang="scss">
.automation-guide-dialog {
  overflow: hidden;
  border-radius: $ui-overlay-radius;
  background: $ui-surface-soft;
  color: $ui-text;
  font: #{$ui-font-size-body}/ 1.5 $ui-font-body;
}
.automation-guide-tabs {
  padding: 0 $ui-space-20;
  border-bottom: 1px solid $ui-line;
  background: $ui-surface;
}
.automation-guide-body {
  max-height: 72vh;
  padding: $ui-space-20;
  overflow-y: auto;
}
.automation-guide-intro {
  display: flex;
  flex-direction: column;
  gap: $ui-space-4;
  margin-bottom: $ui-space-16;
  padding: $ui-space-16;
  border: 1px solid $ui-line;
  border-radius: $ui-panel-radius;
  background: $ui-surface;
}
.automation-guide-intro strong,
.automation-guide-card h3 {
  font-family: $ui-font-heading;
}
.automation-guide-intro span,
.automation-guide-card p,
.automation-guide-card li,
.automation-guide-roles dd,
.automation-guide-note {
  color: $ui-muted;
}
.automation-guide-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: $ui-space-16;
}
.automation-guide-card {
  display: flex;
  align-items: flex-start;
  gap: $ui-space-12;
  padding: $ui-space-16;
  border: 1px solid $ui-line;
  border-radius: $ui-panel-radius;
  background: $ui-surface;
}
.automation-guide-card h3,
.automation-guide-card p,
.automation-guide-card ul,
.automation-guide-roles {
  margin-top: 0;
}
.automation-guide-card h3 {
  margin-bottom: $ui-space-8;
  font-size: 18px;
}
.automation-guide-card p:last-child,
.automation-guide-card ul:last-child {
  margin-bottom: 0;
}
.automation-guide-card ul {
  padding-left: $ui-space-20;
}
.automation-guide-step {
  display: inline-flex;
  width: 28px;
  height: 28px;
  flex: 0 0 28px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: $ui-blue-soft;
  color: $ui-blue;
  font-weight: 700;
}
.automation-guide-roles {
  margin-bottom: 0;
}
.automation-guide-roles > div + div {
  margin-top: $ui-space-12;
}
.automation-guide-roles dt {
  font-weight: 700;
}
.automation-guide-roles dd {
  margin: $ui-space-4 0 0;
}
.automation-guide-note {
  margin: 0 0 $ui-space-16;
}
.automation-legend-levels {
  display: flex;
  flex-wrap: wrap;
  gap: $ui-space-16;
  margin-bottom: $ui-space-16;
  color: $ui-muted;
}
.automation-legend-levels > span {
  display: inline-flex;
  align-items: center;
  gap: $ui-space-8;
}
.automation-legend-group {
  border-top: 1px solid $ui-line;
}
.automation-legend-group summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $ui-space-12;
  padding: $ui-space-16 0;
  cursor: pointer;
}
.automation-legend-group summary span {
  color: $ui-muted;
  font-size: $ui-font-size-metadata;
}
.automation-table-scroll {
  max-width: 100%;
  overflow-x: auto;
}
.automation-legend-table {
  width: 100%;
  min-width: 1000px;
  margin-bottom: $ui-space-16;
  border-collapse: collapse;
  background: $ui-surface;
}
.automation-legend-table th,
.automation-legend-table td {
  padding: $ui-space-12;
  border-bottom: 1px solid $ui-line;
  text-align: left;
}
.automation-legend-table th {
  color: $ui-muted;
  font-size: $ui-font-size-metadata;
}
.automation-legend-table td {
  vertical-align: top;
  white-space: normal;
}
.automation-legend-table td:first-child {
  min-width: 300px;
}
.automation-legend-table td:first-child strong,
.automation-legend-table td:first-child small,
.automation-legend-table td:first-child span {
  display: block;
}
.automation-legend-table td:first-child small {
  margin: 2px 0 $ui-space-4;
  color: $ui-muted;
  font-family: monospace;
}
@media (max-width: 900px) {
  .automation-guide-grid {
    grid-template-columns: 1fr;
  }
}
</style>
